import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "@/components/site/kit";
import { Reveal } from "@/components/motion/primitives";
import { TOOLS } from "@/components/tools/registry";
import MANIFEST from "@/lib/tools_manifest.json";
import { getToolsCatalog } from "@/lib/api";
import AffiliateStrip from "@/components/site/AffiliateStrip";

const SUBCAT = Object.fromEntries(MANIFEST.map((t) => [t.slug, t.subcategory]));
// Generated tools carry their own subcategory on the tool object (ToolCard falls back to it).
import { isPro, startProCheckout, restorePro, getProEmail } from "@/lib/pro";
import { toast } from "sonner";
import { ArrowUpRight, Sparkles, Check, RotateCw, Mail, Briefcase, Users, PenLine, Wrench, MapPin } from "lucide-react";
import SEO, { breadcrumbSchema } from "@/components/site/SEO";

const ToolCard = ({ tool, i }) => (
  <Reveal delay={(i % 3) * 0.06} className={tool.span}>
    <Link to={`/tools/${tool.slug}`} data-testid={`tool-card-${tool.slug}`} className="group flex flex-col h-full bg-white rounded-2xl border border-slate-100 soft-shadow hover-lift p-7">
      <div className="flex items-start justify-between">
        <div className="w-12 h-12 rounded-xl bg-indigo-50 flex items-center justify-center"><tool.icon className="w-6 h-6 text-indigo-600" strokeWidth={1.6} /></div>
        <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 border border-slate-200 rounded-full px-2 py-0.5">{SUBCAT[tool.slug] || tool.subcategory || tool.category}</span>
      </div>
      <h3 className="font-display text-xl mt-5 group-hover:text-indigo-600 transition-colors">{tool.name}</h3>
      <p className="text-slate-600 text-sm mt-2 leading-relaxed flex-1">{tool.desc}</p>
      <span className="mt-5 inline-flex items-center gap-1 text-sm font-medium text-indigo-600">Open tool <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" /></span>
    </Link>
  </Reveal>
);

export default function Tools() {
  const [genTools, setGenTools] = useState([]);
  useEffect(() => {
    getToolsCatalog()
      .then((d) => setGenTools((d.tools || []).filter((t) => t.generated)))
      .catch(() => {});
  }, []);
  const ALL = useMemo(() => {
    const bySlug = new Map();
    for (const t of [...TOOLS, ...genTools.map((t) => ({ ...t, icon: Sparkles }))]) {
      if (!bySlug.has(t.slug)) bySlug.set(t.slug, t);  // static tools win on slug collision
    }
    return Array.from(bySlug.values());
  }, [genTools]);
  const pro = isPro();
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [email, setEmail] = useState(getProEmail());
  const [busy, setBusy] = useState(false);
  const categories = useMemo(() => ["All", ...Array.from(new Set(ALL.map((t) => t.category)))], [ALL]);
  const [cat, setCat] = useState("All");
  const [query, setQuery] = useState("");
  const filtered = useMemo(() => {
    let list = cat === "All" ? ALL : ALL.filter((t) => t.category === cat);
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter((t) => t.name.toLowerCase().includes(q) || t.desc.toLowerCase().includes(q));
    }
    return list;
  }, [cat, query, ALL]);

  // category → subcategory → tools (manifest is the single source of truth)
  const grouped = useMemo(() => {
    const map = {};
    for (const t of ALL) {
      const sub = SUBCAT[t.slug] || "General";
      (map[t.category] ??= {})[sub] ??= [];
      map[t.category][sub].push(t);
    }
    return map;
  }, [ALL]);

  const doRestore = async () => {
    if (!email || !email.includes("@")) return toast.error("Enter a valid email");
    setBusy(true);
    try {
      const r = await restorePro(email.trim().toLowerCase());
      if (r.pro) {
        toast.success("Welcome back — Pro unlocked on this device");
        setRestoreOpen(false);
        setTimeout(() => window.location.reload(), 400);
      } else {
        toast.error("No Pro account found for that email");
      }
    } catch { toast.error("Something went wrong"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="tools-page">
      <SEO
        title="100 Free Web Tools — Calculators, Converters, Generators & SEO | Julius Mason"
        description="A free suite of ~100 browser tools: calculators, converters, text tools, generators, SEO helpers, AI translator and more — no signup, no install."
        path="/tools"
        lang="en"
        keywords="free web tools, online calculators, converters, generators, seo tools, text tools"
        schema={breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Free Tools", path: "/tools" },
        ])}
      />
      <PageHeader
        chapter="Tools / Free Utilities"
        titleLines={["Free tools to", "get things done."]}
        description={`${TOOLS.length}+ genuinely useful web tools — free to use, with an optional Pro unlock for unlimited access and no watermarks.`}
      />

      <section className="py-12 md:py-20">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12">
          {!pro && (
            <Reveal className="mb-10">
              <div data-testid="pro-banner" className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border border-indigo-100 bg-indigo-50/60 rounded-2xl p-6">
                <div>
                  <p className="overline mb-1">Pro · One-time $9</p>
                  <h3 className="font-display text-2xl">Unlock every tool, unlimited.</h3>
                  <div className="flex flex-wrap gap-x-6 gap-y-1 mt-2 text-sm text-slate-600">
                    <span className="inline-flex items-center gap-1.5"><Check className="w-4 h-4 text-indigo-600" /> No daily limits</span>
                    <span className="inline-flex items-center gap-1.5"><Check className="w-4 h-4 text-indigo-600" /> No watermarks</span>
                    <span className="inline-flex items-center gap-1.5"><Check className="w-4 h-4 text-indigo-600" /> Priority new tools</span>
                  </div>
                </div>
                <button data-testid="tools-go-pro" onClick={() => startProCheckout()} className="inline-flex items-center gap-2 bg-indigo-600 text-white rounded-full px-6 py-3 font-medium hover:bg-indigo-700 active:scale-95 transition whitespace-nowrap">
                  <Sparkles className="w-4 h-4" /> Go Pro
                </button>
              </div>
            </Reveal>
          )}

          {!pro && (
            <div className="flex items-center justify-between mb-8 text-sm text-slate-500">
              <span>Already purchased Pro on another device?</span>
              <button data-testid="tools-restore-pro" onClick={() => setRestoreOpen(true)} className="inline-flex items-center gap-1.5 font-medium text-indigo-600 hover:underline">
                <RotateCw className="w-3.5 h-3.5" /> Restore Pro
              </button>
            </div>
          )}

          {restoreOpen && (
            <div className="fixed inset-0 z-[300] bg-black/60 flex items-center justify-center p-4" onClick={() => setRestoreOpen(false)}>
              <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-md p-8" data-testid="restore-modal">
                <div className="flex items-center gap-3 mb-2"><Mail className="w-6 h-6 text-indigo-600" /><h3 className="font-display text-2xl">Restore Pro</h3></div>
                <p className="text-sm text-slate-500 mb-5">Enter the email you used to purchase Pro. We&apos;ll unlock every tool on this device instantly.</p>
                <input data-testid="restore-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" className="w-full border border-slate-200 rounded-lg px-4 py-3 outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 mb-4" />
                <div className="flex gap-3">
                  <button onClick={() => setRestoreOpen(false)} className="flex-1 border border-slate-200 rounded-lg py-3 font-medium hover:bg-slate-50">Cancel</button>
                  <button data-testid="restore-confirm" onClick={doRestore} disabled={busy} className="flex-1 bg-indigo-600 text-white rounded-lg py-3 font-medium hover:bg-indigo-700 disabled:opacity-60">{busy ? "Checking…" : "Restore"}</button>
                </div>
              </div>
            </div>
          )}

          <Link to="/tools/floor-planner" data-testid="floor-planner-banner"
            className="group block mb-10 border border-amber-800/20 bg-gradient-to-r from-amber-50 to-orange-50/40 p-6 md:p-8 hover:shadow-lg transition">
            <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-8">
              <div className="flex-1">
                <p className="font-mono text-[11px] uppercase tracking-widest text-amber-700 mb-2">Nouveau · 3D</p>
                <h2 className="font-display text-2xl md:text-3xl mb-2">Studio Floor Planner — 2D → 3D → PDF</h2>
                <p className="text-sm text-slate-600 max-w-2xl">
                  Chargez un plan, calibrez l'échelle réelle, placez les packs meublés Makasa (décret 2015-981),
                  visualisez en 3D isométrique et exportez un rapport PDF prêt à imprimer.
                </p>
              </div>
              <span className="inline-flex items-center gap-2 bg-amber-700 text-white px-5 py-3 text-sm font-semibold group-hover:bg-amber-800 transition self-start md:self-center">
                Ouvrir l'outil <span aria-hidden>→</span>
              </span>
            </div>
          </Link>

          <div className="mb-6" data-testid="tools-search-wrap">
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`Search all ${TOOLS.length} tools…`}
              aria-label="Search tools"
              data-testid="tools-search"
              className="w-full max-w-md border border-slate-200 bg-white rounded-full px-5 py-3 text-sm outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
            />
          </div>
          <div className="flex flex-wrap gap-2 mb-10" data-testid="tools-categories">
            {categories.map((c) => (
              <button
                key={c}
                data-testid={`tools-cat-${c.toLowerCase()}`}
                onClick={() => { setCat(c); setQuery(""); }}
                className={`text-sm rounded-full px-4 py-1.5 border transition ${cat === c ? "bg-indigo-600 text-white border-indigo-600" : "border-slate-200 text-slate-600 hover:border-indigo-300 hover:text-indigo-600"}`}
              >{c}</button>
            ))}
          </div>

          {/* Searching → flat results. Browsing → category sections, each
              broken into subcategory groups so tools are easy to find. */}
          {query.trim() ? (
            <div className="grid md:grid-cols-3 gap-6" data-testid="tools-flat-grid">
              {filtered.map((tool, i) => <ToolCard key={tool.slug} tool={tool} i={i} />)}
              {filtered.length === 0 && <p className="text-slate-500 col-span-3 py-8">No tools match "{query}" — try a broader word.</p>}
            </div>
          ) : (
            <div className="space-y-16" data-testid="tools-grouped">
              {Object.entries(grouped)
                .filter(([c]) => cat === "All" || cat === c)
                .map(([category, subs]) => (
                  <div key={category} data-testid={`tools-group-${category.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    {cat === "All" && (
                      <div className="flex items-baseline gap-4 mb-8 border-b border-slate-200 pb-4">
                        <h2 className="font-display text-3xl md:text-4xl">{category}</h2>
                        <span className="font-mono text-xs text-slate-400 uppercase tracking-widest">
                          {Object.values(subs).flat().length} tools
                        </span>
                      </div>
                    )}
                    <div className="space-y-10">
                      {Object.entries(subs).map(([sub, tools]) => (
                        <div key={sub} data-testid={`tools-sub-${sub.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                          <p className="font-mono text-xs uppercase tracking-widest text-indigo-600 mb-5">{sub}</p>
                          <div className="grid md:grid-cols-3 gap-6">
                            {tools.map((tool, i) => <ToolCard key={tool.slug} tool={tool} i={i} />)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
            </div>
          )}

          <div className="mt-20">
            <AffiliateStrip
              title="Level up with these tools"
              subtitle="Deeper software recommendations, curated by Julius. Some are affiliate links — same price for you."
              limit={6}
              variant="grid"
            />
          </div>
        </div>
      </section>

      {/* Discover more — mirrors the Home page grid so Tools visitors flow
          naturally into Portfolio, Recommended and the rest of the site. */}
      <section className="py-24 md:py-28 border-t border-slate-100 bg-slate-50/50" data-testid="tools-discover-more">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="mb-12 max-w-2xl">
            <p className="overline text-indigo-600 mb-3">More of the site</p>
            <h2 className="font-display text-4xl md:text-5xl leading-tight">Everything else,<br />one click away.</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { to: "/aveno",   icon: Briefcase,       label: "AVENO",       desc: "My own goods brand — apparel, drinkware, tech and more." },
              { to: "/shop", icon: Wrench,          label: "Shop",     desc: "My hand-picked tools, books and gear — affiliate picks." },
              { to: "/build",   icon: Sparkles,        label: "Website Previewer",  desc: "Describe your project and watch a working preview generate live." },
              { to: "/blog",        icon: PenLine,         label: "Blog",            desc: "Guides on websites, pricing, growth, and every tool here." },
              { to: "/clients",     icon: Users,           label: "Clients",         desc: "The businesses I've had the honor of helping grow." },
              { to: "/web-design", icon: MapPin, label: "Your Market", desc: "Web design for your country, city, or industry — worldwide." },
            ].map(({ to, icon: Icon, label, desc }, i) => (
              <Reveal key={to} delay={i * 0.04}>
                <Link
                  to={to}
                  data-testid={`tools-discover-${label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="group flex flex-col h-full border border-slate-200 rounded-2xl bg-white p-6 hover:border-indigo-300 hover:shadow-sm transition-all"
                >
                  <div className="flex items-start justify-between mb-6">
                    <div className="w-10 h-10 grid place-items-center bg-indigo-50 text-indigo-600 rounded-lg">
                      <Icon className="w-5 h-5" strokeWidth={1.6} />
                    </div>
                    <ArrowUpRight className="w-4 h-4 text-slate-400 group-hover:text-indigo-600 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                  </div>
                  <h3 className="font-display text-2xl leading-tight group-hover:text-indigo-600 transition-colors">{label}</h3>
                  <p className="text-sm text-slate-500 mt-2 flex-1">{desc}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
